import { contactDisplayName } from '@taki/data-model';
import type { Asset, Booking, Contact, WorkOrder } from '@taki/data-model';
import type { Option } from '@taki/ui';

/**
 * HELPERS METIER de l'app Maintenance : catalogues d'options, formatage et
 * CALCUL DE COUT. Aucun JSX, aucun acces SDK : ce module est importe par
 * l'ecran, par les reglages et par le jeu de demonstration, il ne doit donc
 * dependre de rien d'autre que de ses arguments.
 *
 * Les catalogues (`Option[]`) vivent ICI et nulle part ailleurs : ce sont eux
 * que les bindings passent a `options()` (obligatoire pour `status`, `groupKey`
 * et `lane`) et que les reglages affichent. Le `tone` est le SEUL vocabulaire
 * de couleur autorise (neutral | accent | success | danger), jamais un hex.
 */

/* ------------------------------------------------------------------ */
/* Catalogues (vocabulaire FIGE du pack de demonstration)              */
/* ------------------------------------------------------------------ */

/** Avancement d'un SIGNALEMENT (famille `tickets`, type local `panne`). */
export const TICKET_STATUS_OPTIONS: Option[] = [
  { value: 'ouvert', label: 'Ouvert', tone: 'danger' },
  { value: 'en_cours', label: 'En cours', tone: 'accent' },
  { value: 'resolu', label: 'Resolu', tone: 'success' },
  { value: 'clos', label: 'Clos', tone: 'neutral' },
];

/** Urgence d'un signalement. */
export const TICKET_PRIORITY_OPTIONS: Option[] = [
  { value: 'basse', label: 'Basse', tone: 'neutral' },
  { value: 'normale', label: 'Normale', tone: 'neutral' },
  { value: 'haute', label: 'Haute', tone: 'accent' },
  { value: 'urgente', label: 'Urgente', tone: 'danger' },
];

/** Priorites considerees comme urgentes par la statistique "urgents". */
export const URGENT_PRIORITIES: readonly string[] = ['haute', 'urgente'];

/**
 * LA CAUSE : le champ qui porte la promesse metier. Il dit A CAUSE DE QUI le
 * materiel est casse, donc qui paie la reparation.
 */
export const CAUSE_OPTIONS: Option[] = [
  { value: 'usure', label: 'Usure normale', tone: 'neutral' },
  { value: 'client', label: 'Faute du client', tone: 'danger' },
  { value: 'accident_interne', label: 'Accident interne', tone: 'accent' },
  { value: 'defaut_usine', label: 'Defaut usine', tone: 'accent' },
];

/** Cause imputable au client : celle qui alimente la refacturation. */
export const CAUSE_CLIENT = 'client';

/** Par ou le signalement est arrive. */
export const CHANNEL_OPTIONS: Option[] = [
  { value: 'retour_materiel', label: 'Retour materiel', tone: 'neutral' },
  { value: 'atelier', label: 'Atelier', tone: 'neutral' },
  { value: 'client', label: 'Client', tone: 'neutral' },
];

/** Avancement d'une INTERVENTION (famille `workOrders`, type local `reparation`). */
export const WORK_ORDER_STATUS_OPTIONS: Option[] = [
  { value: 'a_planifier', label: 'A planifier', tone: 'neutral' },
  { value: 'en_cours', label: 'En cours', tone: 'accent' },
  { value: 'en_attente_piece', label: 'En attente de piece', tone: 'danger' },
  { value: 'termine', label: 'Termine', tone: 'success' },
];

/** Qui supporte le cout de l'intervention. */
export const CHARGE_TO_OPTIONS: Option[] = [
  { value: 'client', label: 'Client', tone: 'accent' },
  { value: 'interne', label: 'Interne', tone: 'neutral' },
];

/**
 * `type` local de la famille `bookings`, pose par l'app Reservations. L'app
 * Maintenance LIT cette famille (jamais d'ecriture : les locations sont le
 * domaine de l'app Reservations) pour NOMMER la location d'origine d'un
 * signalement. Le parc coexiste avec les creneaux d'autres apps : on ne
 * propose donc jamais autre chose qu'une location.
 */
export const BOOKING_TYPE = 'location';

/** Categories du parc (attribut du materiel, pas un discriminant de famille). */
export const ASSET_CATEGORY_OPTIONS: Option[] = [
  { value: 'camera', label: 'Camera', tone: 'neutral' },
  { value: 'optique', label: 'Optique', tone: 'neutral' },
  { value: 'lumiere', label: 'Lumiere', tone: 'neutral' },
  { value: 'son', label: 'Son', tone: 'neutral' },
  { value: 'machinerie', label: 'Machinerie', tone: 'neutral' },
];

/** Libelle d'une valeur dans un catalogue ; la valeur brute si elle en sort. */
export function labelOf(options: Option[], value: string | null | undefined): string {
  if (value == null || value === '') return '';
  return options.find((option) => option.value === value)?.label ?? value;
}

/* ------------------------------------------------------------------ */
/* Lecture du sac customFields                                         */
/* ------------------------------------------------------------------ */

/** Tout document du socle porte un sac `customFields` optionnel. */
interface HasCustomFields {
  customFields?: Record<string, unknown> | undefined;
}

/** Valeur texte d'un champ personnalise, ou null (jamais "undefined" a l'ecran). */
export function customText(record: HasCustomFields, key: string): string | null {
  const value = record.customFields?.[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

/** Valeur numerique d'un champ personnalise, ou null. */
export function customNumber(record: HasCustomFields, key: string): number | null {
  const value = record.customFields?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Valeur booleenne d'un champ personnalise (absente = false). */
export function customBool(record: HasCustomFields, key: string): boolean {
  return record.customFields?.[key] === true;
}

/** Liste de chaines d'un champ personnalise, vide par defaut (jamais null). */
export function customStringList(record: HasCustomFields, key: string): string[] {
  const value = record.customFields?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

/* ------------------------------------------------------------------ */
/* Index des references (materiel et clients), alimente par l'ecran    */
/* ------------------------------------------------------------------ */

/**
 * Les familles `tickets` et `workOrders` ne portent que des IDS vers le
 * materiel, le client et la location d'origine. Pour nommer ces ids dans les
 * vues, l'ecran charge les `assets` de type `materiel`, les `contacts` et les
 * `bookings` de type `location` DANS son chargeur, puis alimente cet index
 * AVANT que les enregistrements ne soient poses.
 *
 * L'index vit au niveau du module pour que les bindings (declares une fois)
 * puissent le lire sans recevoir de contexte React : une vue ne recoit jamais
 * que des props, et un binding ne recoit que son enregistrement.
 */
interface AssetLabel {
  name: string;
  category: string;
}

/** Ce qu'on sait dire d'une location : sa ligne complete, et son seul projet. */
interface BookingLabels {
  full: string;
  project: string;
}

const assetIndex = new Map<string, AssetLabel>();
const contactIndex = new Map<string, string>();
const bookingIndex = new Map<string, BookingLabels>();

/** Nom lisible d'un client. Le helper du socle gere personne ET organisation. */
export function contactLabel(contact: Contact): string {
  const full = contactDisplayName(contact);
  return full === 'Contact sans nom' ? 'Client sans nom' : full;
}

/** (Re)construit l'index des references. Appele par le chargeur de l'ecran. */
export function indexReferences(
  assets: Asset[],
  contacts: Contact[],
  bookings: Booking[],
): void {
  assetIndex.clear();
  for (const asset of assets) {
    assetIndex.set(asset.id, {
      name: asset.name,
      category: customText(asset, 'category') ?? '',
    });
  }
  contactIndex.clear();
  for (const contact of contacts) {
    contactIndex.set(contact.id, contactLabel(contact));
  }
  // Les locations APRES les clients : leur libelle nomme le client, il a donc
  // besoin d'un carnet d'adresses deja rempli.
  bookingIndex.clear();
  for (const booking of bookings) {
    bookingIndex.set(booking.id, {
      full: bookingOptionLabel(booking),
      project: bookingProjectName(booking),
    });
  }
}

/** Nom du materiel, ou null si l'id ne pointe rien de connu. */
export function assetName(assetId: string | null): string | null {
  if (assetId == null) return null;
  return assetIndex.get(assetId)?.name ?? null;
}

/** Categorie du materiel (valeur brute du catalogue), ou null. */
export function assetCategory(assetId: string | null): string | null {
  if (assetId == null) return null;
  const category = assetIndex.get(assetId)?.category ?? '';
  return category === '' ? null : category;
}

/** Nom du client, ou null si l'id ne pointe rien de connu. */
export function contactName(contactId: string | null | undefined): string | null {
  if (contactId == null || contactId === '') return null;
  return contactIndex.get(contactId) ?? null;
}

/* ---- Les locations, LUES seulement (le domaine de l'app Reservations) ---- */

/** Nom du projet de tournage : c'est par la qu'un loueur reconnait sa location. */
export function bookingProjectName(booking: Booking): string {
  return customText(booking, 'projectName') ?? 'Location sans projet';
}

/**
 * Le libelle LISIBLE d'une location : projet, client, periode, dans cet ordre.
 * "Spot 30s Mobilite, Petrusse Media, du 12/07 au 15/07". C'est ce que la liste
 * deroulante du formulaire propose, et ce que la fiche affiche : jamais un
 * identifiant Firestore, que personne ne sait lire ni taper.
 */
export function bookingOptionLabel(booking: Booking): string {
  const period =
    booking.startAt == null || booking.endAt == null
      ? ''
      : `du ${formatDayMonth(booking.startAt)} au ${formatDayMonth(booking.endAt)}`;
  return [bookingProjectName(booking), contactName(booking.contactId) ?? '', period]
    .filter((part) => part !== '')
    .join(', ');
}

/** Libelle complet de la location d'origine, ou null si l'id n'est pas connu. */
export function bookingLabel(bookingId: string | null | undefined): string | null {
  if (bookingId == null || bookingId === '') return null;
  return bookingIndex.get(bookingId)?.full ?? null;
}

/** Nom du projet de la location d'origine, ou null : la forme courte des listes. */
export function bookingProject(bookingId: string | null | undefined): string | null {
  if (bookingId == null || bookingId === '') return null;
  return bookingIndex.get(bookingId)?.project ?? null;
}

/** Ids des materiels engages par une location (`customFields.assetIds`). */
export function bookingAssetIds(booking: Booking): string[] {
  return customStringList(booking, 'assetIds');
}

/**
 * L'instant qui date une location pour le classement : son depart, sinon sa
 * creation (le champ `startAt` de la famille booking est optionnel).
 */
function bookingTime(booking: Booking): number {
  return (booking.startAt ?? booking.createdAt).getTime();
}

/** Les locations, LES PLUS RECENTES D'ABORD : l'ordre de la liste deroulante. */
export function sortBookingsByRecency(bookings: Booking[]): Booking[] {
  return [...bookings].sort((a, b) => bookingTime(b) - bookingTime(a));
}

/* ------------------------------------------------------------------ */
/* LE CALCUL DE COUT (le coeur de la promesse de refacturation)        */
/* ------------------------------------------------------------------ */

/** Une ligne de pieces consommee par une intervention. */
export interface PartLine {
  label: string;
  qty: number;
  unitPrice: number;
}

/**
 * Les lignes de pieces d'une intervention. Le socle les stocke en
 * `Record<string, unknown>[]` (liste structuree libre) : on les relit ici de
 * facon defensive, une ligne mal formee ne casse jamais l'affichage.
 */
export function partLinesOf(order: WorkOrder): PartLine[] {
  const raw = order.partLines ?? [];
  return raw.map((line) => ({
    label: typeof line['label'] === 'string' && line['label'] !== '' ? line['label'] : 'Piece',
    qty: typeof line['qty'] === 'number' ? line['qty'] : 0,
    unitPrice: typeof line['unitPrice'] === 'number' ? line['unitPrice'] : 0,
  }));
}

/** Total des pieces : somme de (quantite x prix unitaire). */
export function partsTotal(lines: PartLine[]): number {
  return lines.reduce((sum, line) => sum + line.qty * line.unitPrice, 0);
}

/** Main d'oeuvre : minutes / 60 x taux horaire (pref de l'app). */
export function laborCost(laborMinutes: number | undefined, rate: number): number {
  return ((laborMinutes ?? 0) / 60) * rate;
}

/** Cout total d'une intervention : pieces + main d'oeuvre. */
export function totalCost(order: WorkOrder, rate: number): number {
  return partsTotal(partLinesOf(order)) + laborCost(order.laborMinutes, rate);
}

/** Cout total d'une liste d'interventions. */
export function sumCost(orders: WorkOrder[], rate: number): number {
  return orders.reduce((sum, order) => sum + totalCost(order, rate), 0);
}

/* ------------------------------------------------------------------ */
/* Formatage                                                           */
/* ------------------------------------------------------------------ */

/** Un montant en euros, arrondi a l'euro (la demo ne facture pas les centimes). */
export function formatEuros(value: number): string {
  return `${Math.round(value)} EUR`;
}

/** Une duree de main d'oeuvre lisible : "45 min", "1 h 30", "2 h". */
export function formatMinutes(laborMinutes: number | undefined): string {
  const minutes = laborMinutes ?? 0;
  if (minutes <= 0) return 'Aucune';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest}`;
}

/** Une date au format court du poste. */
export function formatDay(value: Date | null | undefined): string {
  return value == null ? '' : value.toLocaleDateString();
}

/**
 * Une date en "12/07" : la forme courte des periodes de location, la meme que
 * celle du planning de l'app Reservations. Une periode se lit d'un coup d'oeil
 * dans une liste deroulante, l'annee y serait du bruit.
 */
export function formatDayMonth(value: Date | null | undefined): string {
  if (value == null) return '';
  const day = String(value.getDate()).padStart(2, '0');
  const month = String(value.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}`;
}

/** Initiales de la categorie du materiel, pour la pastille de tete de ligne. */
export function categoryInitials(category: string | null): string {
  if (category == null || category === '') return '?';
  return category.slice(0, 2).toUpperCase();
}

/** Coupe un texte long sans couper un mot en deux. */
export function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut).trimEnd()}...`;
}

/* ------------------------------------------------------------------ */
/* Dates                                                               */
/* ------------------------------------------------------------------ */

/**
 * Convertit une chaine ISO 8601 (stockee dans `customFields`) en Date.
 * Les customFields n'acceptent que des primitives : une date-heure metier y
 * vit donc en chaine ISO, et c'est le `get` du role qui la remet en Date.
 */
export function isoToDate(value: string | null): Date | null {
  if (value == null || value === '') return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Date issue d'un champ de formulaire `type="date"` ("2026-08-04").
 * On la pose a midi pour qu'aucun decalage de fuseau ne fasse glisser le jour.
 */
export function dateFromInput(value: string): Date | null {
  if (value === '') return null;
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Valeur d'un champ `type="date"` a partir d'une Date. */
export function dateToInput(value: Date): string {
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${value.getFullYear()}-${month}-${day}`;
}

/** Le mois civil de `reference` contient-il `value` ? (statistique du mois) */
export function isSameMonth(value: Date | null | undefined, reference: Date): boolean {
  if (value == null) return false;
  return (
    value.getFullYear() === reference.getFullYear() && value.getMonth() === reference.getMonth()
  );
}
